import { VIEW_AS_MODES, type ViewAs } from '@nsb/core';
import { QueryClient, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { saved } from './saved.ts';

/** The API lives at /api on the same origin (Vite proxy in dev, Netlify proxy in production). */

const VIEW_AS_KEY = 'nsb.viewAs';

/**
 * Developer mode: the role an admin wants the API to apply (sent as X-View-As).
 * Remembered per browser; the API ignores it for anyone who is not an admin.
 */
let viewAs: ViewAs | null = readViewAs();

function readViewAs(): ViewAs | null {
  try {
    const stored = localStorage.getItem(VIEW_AS_KEY);
    return VIEW_AS_MODES.find((m) => m === stored) ?? null;
  } catch {
    return null;
  }
}

export function setViewAs(mode: ViewAs | null) {
  viewAs = mode;
  try {
    if (mode) localStorage.setItem(VIEW_AS_KEY, mode);
    else localStorage.removeItem(VIEW_AS_KEY);
  } catch {
    // Storage unavailable (private window): the choice lasts until the page reloads.
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: Record<string, unknown> | null;
  constructor(status: number, code: string, message: string, body: Record<string, unknown> | null) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/**
 * The notice a change gets once the API has accepted it (src/saved.ts; Jason, 2026-10-07: "any time a user clicks save
 * or set ... or anywhere something changes dynamically, there needs to be something pop up that says updated or save
 * that is clear"). Every POST, PUT, PATCH and DELETE that answers ok shows it; a read never does, and a refusal never
 * does (its words stay where the page shows them). The words, in this order, first match wins:
 *
 * 1. The caller's own: `notice: 'Sent'` for words of its own, `notice: false` for none.
 * 2. QUIET: writes nobody made on purpose, or whose page already says how they went (each rule says why).
 * 3. The rules in NOTICE_RULES, for a change that "Saved" would describe wrongly: one held in a draft, a bundle loaded,
 *    a project or report deleted, a painting or summary started, and the changes named by their last word (undo,
 *    discard, check, revoke, …).
 * 4. Otherwise "Removed" for a DELETE, "Uploaded" for a file sent as itself, and "Saved" for anything else.
 *
 * Paths are matched as callers write them, after /api and with their query.
 */
export interface NoticeRule {
  method?: string;
  path: RegExp;
  /** The notice's words; null for none. */
  words: string | null;
  why: string;
}

const PROJECT = String.raw`^/projects/[^/?]+`;

export const QUIET: NoticeRule[] = [
  { path: /^\/auth\//, words: null, why: 'Signing in or out, a sign-in link, a reset: the sign-in page answers for itself, and signing in or out leaves the page.' },
  { path: /^\/dev\/sign-in(\?|$)/, words: null, why: 'This machine’s own sign-in, the same as signing in.' },
  { path: new RegExp(`${PROJECT}/seen(\\?|$)`), words: null, why: 'By itself: opening a lead made from an email marks it seen.' },
  { path: new RegExp(`${PROJECT}/introduction(\\?|$)`), words: null, why: 'By itself: opening or closing the Introduction records how far it was read.' },
  { method: 'POST', path: /^\/feedback(\?|$)/, words: null, why: 'Filing a report: the sheet says Sent, with the report’s number.' },
  { path: /^\/integrations\/[^/?]+\/connect(\?|$)/, words: null, why: 'Connecting leaves for the provider’s own consent screen.' },
  { method: 'POST', path: /^\/oauth\/authorize(\?|$)/, words: null, why: 'Allowing Claude, or not, leaves the page for Claude’s own.' },
  { path: /^\/integrations\/(?:gmail|resend)\/test(\?|$)/, words: null, why: 'Send me a test: its own line says whether the message went (the API answers either way).' },
  { path: new RegExp(`${PROJECT}/invites(\\?|$)`), words: null, why: 'Invitations: its own lines say who each went to and who it did not reach (the API answers either way).' },
  { path: /^\/bundles\?(.*&)?dryRun=true(&|$)/, words: null, why: 'A dry run changes nothing: the page lists what loading the bundle would add.' },
  {
    path: new RegExp(`${PROJECT}/process/steps/[^/?]+/carry-back/told(\\?|$)`),
    words: null,
    why: 'Whose clients carrying a step back would tell: a look that changes nothing, the warning naming them (Jason, 2026-10-08).',
  },
];

/** The last word of a change's path, and what it did. */
const DONE_WORDS: Record<string, string> = {
  save: 'Saved',
  undo: 'Undone',
  discard: 'Discarded',
  check: 'Checked',
  share: 'Shared',
  start: 'Started',
  pause: 'Paused',
  resume: 'Resumed',
  cancel: 'Cancelled',
  archive: 'Archived',
  void: 'Voided',
  withdraw: 'Withdrawn',
  revoke: 'Revoked',
  disconnect: 'Disconnected',
};

export const NOTICE_RULES: NoticeRule[] = [
  // A job's shape, changed on its Process setup page, is held in its draft until Save the changes (routes/process.ts
  // `shape`): kept, not saved. The rest of what that page sends with ?draft=1 (a task done, a plan) is made at once.
  {
    path: new RegExp(`${PROJECT}/process/(enable|left-out|overrides|put-back|copies|links|steps|steps/[^/?]+/(off|restore|card|back|special))\\?(.*&)?draft=1(&|$)`),
    words: 'Draft kept',
    why: 'A change to the job’s shape, held in its draft until Save the changes.',
  },
  { method: 'PUT', path: /^\/builder(\?|$)/, words: 'Draft kept', why: 'The master tree’s draft, kept as it changes (the page says Draft kept too); Save the changes saves it for projects.' },
  { method: 'POST', path: /^\/builder\/lock-in(\?|$)/, words: 'Saved for projects', why: 'The master tree saved: every project inherits it.' },
  { method: 'PUT', path: /^\/bundles\?/, words: 'Loaded', why: 'A bundle loaded into this portal.' },
  { method: 'DELETE', path: new RegExp(`${PROJECT}(\\?|$)|^/feedback/[^/?]+(\\?|$)`), words: 'Deleted', why: 'A project or a report deleted for good, not taken off.' },
  {
    method: 'POST',
    path: new RegExp(`^/(art/brand|org/art|process/step-icons|trades/icons|patterns/pictures)(\\?|$)|${PROJECT}/art(/|\\?|$)`),
    words: 'Painting started',
    why: 'Paint again, Paint the missing: the pictures come a little later.',
  },
  { method: 'POST', path: new RegExp(`${PROJECT}/reviews/[^/?]+/summary(\\?|$)`), words: 'Started', why: 'Sum it up again: Claude’s summary comes a little later.' },
  { method: 'POST', path: new RegExp(`${PROJECT}/remind(\\?|$)`), words: 'Reminder sent', why: 'Remind them: an email went to the client, nothing was saved (design study 26).' },
  {
    method: 'POST',
    path: /^\/dev\/test-projects\/reset(\?|$)/,
    words: 'Reset started',
    why: 'Reset the test projects: the documented jobs take a few minutes to make again, and the section says where it stands.',
  },
  { method: 'POST', path: /^\/intake\/[^/?]+\/discard(\?|$)/, words: 'Set aside', why: 'Not for the portal: the email is set aside, never discarded, and Undo brings it back.' },
  ...Object.entries(DONE_WORDS).map(([last, words]) => ({ path: new RegExp(`/${last}(\\?|$)`), words, why: `A change named by its last word, ${last}.` })),
];

/** The notice's words for a change that landed, or null for none (the order is above). */
export function noticeWords(method: string, path: string, given?: string | false, fallback?: string): string | null {
  if (given !== undefined) return given || null;
  const m = method.toUpperCase();
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return null;
  for (const rule of [...QUIET, ...NOTICE_RULES]) {
    if ((!rule.method || rule.method === m) && rule.path.test(path)) return rule.words;
  }
  return fallback ?? (m === 'DELETE' ? 'Removed' : 'Saved');
}

function announce(method: string, path: string, given?: string | false, fallback?: string) {
  const words = noticeWords(method, path, given, fallback);
  if (words) saved.show(words);
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown; notice?: string | false } = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (viewAs) headers['x-view-as'] = viewAs;
  const method = opts.method ?? (opts.body === undefined ? 'GET' : 'POST');
  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    credentials: 'same-origin',
  });
  const out = await answer<T>(res);
  announce(method, path, opts.notice);
  return out;
}

/** A file sent as itself: the request body is the file, its type the content type. */
export async function upload<T>(path: string, file: Blob, contentType: string, opts: { notice?: string | false } = {}): Promise<T> {
  const headers: Record<string, string> = { 'content-type': contentType };
  if (viewAs) headers['x-view-as'] = viewAs;
  const res = await fetch(`/api${path}`, { method: 'PUT', headers, body: file, credentials: 'same-origin' });
  const out = await answer<T>(res);
  announce('PUT', path, opts.notice, 'Uploaded');
  return out;
}

/** A file fetched as itself (a GET, so it changes nothing and says nothing): a PDF from Files split into plates (P-36). */
export async function download(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (viewAs) headers['x-view-as'] = viewAs;
  const res = await fetch(`/api${path}`, { headers, credentials: 'same-origin' });
  if (!res.ok) await answer(res);
  return res.blob();
}

async function answer<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const b = (data ?? {}) as Record<string, unknown>;
    throw new ApiError(res.status, String(b.error ?? 'error'), String(b.message ?? res.statusText), data as Record<string, unknown> | null);
  }
  return data as T;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
      staleTime: 10_000,
    },
  },
});

export function useApi<T>(key: QueryKey, path: string | null) {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => api<T>(path!),
    enabled: path !== null,
  });
}

/** A mutation that refreshes the given queries when it lands. */
export function useAction<TBody = unknown, TResult = unknown>(
  fn: (body: TBody) => Promise<TResult>,
  opts: { invalidate?: QueryKey[]; onSuccess?: (r: TResult) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation<TResult, ApiError, TBody>({
    mutationFn: fn,
    onSuccess: async (r) => {
      await Promise.all((opts.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })));
      opts.onSuccess?.(r);
    },
  });
}

export function errorText(e: unknown): string | null {
  if (!e) return null;
  if (e instanceof ApiError) return e.message;
  return 'Something went wrong. Try again.';
}
